import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../utils/api';

/**
 * Load / save JSON blob at POST /api/settlement/:estateId/frame/:frameId
 */
export function useSettlementFrameData(estateId: string | null, frameId: string) {
  const [data, setData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!estateId) {
      setData({});
      return;
    }
    setLoading(true);
    try {
      const res = (await apiService.request(`/api/settlement/${estateId}/frame/${frameId}`, {
        method: 'GET',
      })) as { success?: boolean; data?: { data?: Record<string, unknown> | null } | null };
      const row = res.data;
      const blob = row && typeof row === 'object' && 'data' in row ? row.data : null;
      if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
        setData({ ...blob });
      } else {
        setData({});
      }
    } catch {
      setData({});
    } finally {
      setLoading(false);
    }
  }, [estateId, frameId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (updates: Record<string, unknown>) => {
      const next = { ...data, ...updates };
      setData(next);
      if (!estateId) return false;
      const res = await apiService.request(`/api/settlement/${estateId}/frame/${frameId}`, {
        method: 'POST',
        body: JSON.stringify({ data: next }),
      });
      return !!res.success;
    },
    [data, estateId, frameId]
  );

  return { data, setData, save, refresh, loading };
}
